import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { toast } from 'sonner';
import { invoiceKeys } from '@/lib/query-keys';
import { invoiceService } from '@/services/invoice.service';
import { useInvoiceFilterStore } from '@/stores/invoiceFilterStore';

export function useInvoices() {
  const queryClient = useQueryClient();
  const filters = useInvoiceFilterStore();

  const invoicesQuery = useQuery({
    queryKey: invoiceKeys.list(filters.current),
    queryFn: () => invoiceService.getAll(filters.current),
    placeholderData: keepPreviousData,
  });

  const createInvoice = useMutation({
    mutationFn: (data: Record<string, unknown>) => invoiceService.create(data),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: invoiceKeys.lists() }); toast.success('Invoice created'); },
    onError: () => toast.error('Failed to create invoice'),
  });

  const updateInvoice = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Record<string, unknown> }) => invoiceService.update(id, data),
    onSuccess: (_, { id }) => { queryClient.invalidateQueries({ queryKey: invoiceKeys.lists() }); queryClient.invalidateQueries({ queryKey: invoiceKeys.detail(id) }); toast.success('Invoice updated'); },
    onError: () => toast.error('Failed to update invoice'),
  });

  const deleteInvoice = useMutation({
    mutationFn: (id: string) => invoiceService.delete(id),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: invoiceKeys.lists() }); toast.success('Invoice deleted'); },
    onError: () => toast.error('Failed to delete invoice'),
  });

  const sendInvoice = useMutation({
    mutationFn: (id: string) => invoiceService.send(id),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: invoiceKeys.all }); toast.success('Invoice sent'); },
    onError: () => toast.error('Failed to send invoice'),
  });

  const cancelInvoice = useMutation({
    mutationFn: (id: string) => invoiceService.cancel(id),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: invoiceKeys.all }); toast.success('Invoice cancelled'); },
    onError: () => toast.error('Failed to cancel invoice'),
  });

  const duplicateInvoice = useMutation({
    mutationFn: (id: string) => invoiceService.duplicate(id),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: invoiceKeys.lists() }); toast.success('Invoice duplicated'); },
    onError: () => toast.error('Failed to duplicate invoice'),
  });

  return {
    invoices: invoicesQuery.data?.data ?? [],
    meta: invoicesQuery.data?.meta,
    isLoading: invoicesQuery.isLoading,
    filters,
    createInvoice, updateInvoice, deleteInvoice, sendInvoice, cancelInvoice, duplicateInvoice,
    downloadPdf: (id: string) => invoiceService.downloadPdf(id),
    exportCsv: () => invoiceService.exportCsv(filters.current),
  };
}

export function useInvoice(id: string) {
  const invoiceQuery = useQuery({ queryKey: invoiceKeys.detail(id), queryFn: () => invoiceService.getById(id), enabled: !!id });
  return { invoice: invoiceQuery.data?.data, isLoading: invoiceQuery.isLoading };
}
